import { useEffect, useRef, useCallback, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  City,
  TrainRoute,
  TripLeg,
  StopActions,
  type ExploreFilters,
} from "../data/types";
export type { ExploreFilters } from "../data/types";
export { DEFAULT_EXPLORE_FILTERS } from "../data/types";
import { journeyDaySegments, journeyStationDay, type JourneyDaySegment } from "../services/journeyTime";
import { getCityById } from "../services/cityStore";
import { type POI } from "../services/poiService";

import { MapRenderer, type MarkerCallbacks } from "../services/mapRenderer";
import {
  DAY_COLORS,
  NEUTRAL,
} from "../data/design-tokens";

interface MapViewProps {
  routes: TrainRoute[];
  activeRouteIndex: number;
  selectedStop: City | null;
  stopActions: StopActions;
  viewMode: "search" | "explore";

  tripStops: City[];
  tripLegs: TripLeg[]; // Journey legs with intermediate stations
  /** Pre-computed station → {day, color} lookup from useTripPlanner. */
  tripStationDayMap?: Map<string, { color: string; day: number }>;
  /** Starting day number for the active route (continues from trip). Default 1. */
  routeStartDay?: number;
  /** All POIs for explore mode map dots */
  allPOIs?: POI[];
  /** POIs along the active search route (for search-mode map markers) */
  routePOIs?: POI[];
  /** POIs near trip stops (for search-mode map markers when no active route) */
  tripPOIs?: POI[];
  /** Layer filters for explore mode */
  exploreFilters?: ExploreFilters;
  searchPanelVisible?: boolean;
  tripPanelVisible?: boolean;
  onSelectRoute: (index: number) => void;
  onSelectStop: (station: City) => void;
  onDeselectStop: () => void;
  /** Callback that also reports the screen pixel position of the clicked marker */
  onSelectStopWithPosition?: (station: City, pos: { x: number; y: number }) => void;
  /** Called when the selected stop's screen position changes due to map pan/zoom */
  onSelectedStopPositionUpdate?: (pos: { x: number; y: number } | null) => void;
  /** Connection lines from trip stops to nearby POIs (color per type) */
  poiConnections?: Array<{ from: [number, number]; to: [number, number]; poi: POI }>;
  onAddRouteToTrip?: (route: TrainRoute) => void;
  onSearchConnection?: (from: City, to: City) => void;
  onMapReady?: (controls: { zoomIn: () => void; zoomOut: () => void }) => void;
  /** Callback to expose the raw Leaflet map instance (used by useTrainAnimation) */
  onMapRef?: (map: L.Map) => void;
  /** Set hovered POI for React overlay (replaces Leaflet tooltip) */
  onSetHoveredPOI?: (poi: POI, pos: { x: number; y: number }) => void;
  /** Clear hovered POI overlay */
  onClearHoveredPOI?: () => void;
}



// ─── Tile URLs ──────────────────────────────────────────────────────────

const cartoKey = import.meta.env.VITE_CARTO_BASEMAP_API_KEY;
const TILES_LIGHT = cartoKey
  ? `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(cartoKey)}`
  : "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

// ─── Main MapView Component ────────────────────────────────────────────

export function MapView({
  routes,
  activeRouteIndex,
  selectedStop,
  stopActions,
  viewMode,
  tripStops,
  tripLegs,
  tripStationDayMap,
  routeStartDay,
  allPOIs,
  routePOIs,
  tripPOIs,
  exploreFilters,
  searchPanelVisible = true,
  tripPanelVisible = false,
  onSelectRoute,
  onSelectStop,
  onDeselectStop,
  onSelectStopWithPosition,
  onSelectedStopPositionUpdate,
  onAddRouteToTrip,
  onSearchConnection,
  onMapReady,
  onMapRef,
  poiConnections,
  onSetHoveredPOI,
  onClearHoveredPOI,
}: Readonly<MapViewProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const mgrRef = useRef<MapRenderer | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const prevRouteKeyRef = useRef<string>("");
  const zoomingRef = useRef(false);
  const pendingFitRef = useRef<(() => void) | null>(null);

  // Stable callback refs so we don't re-init the map on every render
  const onSelectRouteRef = useRef(onSelectRoute);
  onSelectRouteRef.current = onSelectRoute;
  const onSelectStopRef = useRef(onSelectStop);
  onSelectStopRef.current = onSelectStop;
  const onDeselectStopRef = useRef(onDeselectStop);
  onDeselectStopRef.current = onDeselectStop;
  const onAddRouteToTripRef = useRef(onAddRouteToTrip);
  onAddRouteToTripRef.current = onAddRouteToTrip;
  const onSelectStopWithPositionRef = useRef(onSelectStopWithPosition);
  onSelectStopWithPositionRef.current = onSelectStopWithPosition;
  const onSelectedStopPositionUpdateRef = useRef(onSelectedStopPositionUpdate);
  onSelectedStopPositionUpdateRef.current = onSelectedStopPositionUpdate;
  const onMapRefRef = useRef(onMapRef);
  onMapRefRef.current = onMapRef;
  const onSetHoveredPOIRef = useRef(onSetHoveredPOI);
  onSetHoveredPOIRef.current = onSetHoveredPOI;
  const onClearHoveredPOIRef = useRef(onClearHoveredPOI);
  onClearHoveredPOIRef.current = onClearHoveredPOI;

  const activeRoute = routes[activeRouteIndex];

  // Day segments for the search-route preview (NOT the saved trip — that comes via tripStationDayMap)
  const daySegments = useMemo((): JourneyDaySegment[] => {
    if (!activeRoute) return [];
    return journeyDaySegments(activeRoute, routeStartDay ?? 1);
  }, [activeRoute, routeStartDay]);

  const activeStationIds = useMemo(
    () => new Set(activeRoute?.stations || []),
    [activeRoute]
  );

  /** City names on the active route — used to deduplicate trip-stop markers
   *  whose IDs (city_key slugs) don't match active-route IDs (HAFAS EVA). */
  const activeCityNames = useMemo(() => {
    const names = new Set<string>();
    for (const id of activeRoute?.stations || []) {
      const st = getCityById(id);
      if (st) names.add(st.city.toLowerCase());
    }
    return names;
  }, [activeRoute]);

  const stationDays = useMemo(() => {
    if (!activeRoute) return new Map<string, number>();
    return new Map(activeRoute.stationData?.map((station, index) =>
      [station.id, journeyStationDay(activeRoute, station, routeStartDay ?? 1, index === 0)]));
  }, [activeRoute, routeStartDay]);

  const getDayColor = useCallback(
    (stationId: string): string => {
      const day = stationDays.get(stationId);
      if (day) return DAY_COLORS[(day - 1) % DAY_COLORS.length];
      for (const seg of [...daySegments].reverse()) {
        if (seg.stationIds.includes(stationId)) return seg.color;
      }
      return DAY_COLORS[0];
    },
    [daySegments, stationDays]
  );

  const getDayNumber = useCallback(
    (stationId: string): number => {
      const day = stationDays.get(stationId);
      if (day) return day;
      for (const seg of [...daySegments].reverse()) {
        if (seg.stationIds.includes(stationId)) return seg.day;
      }
      return 1;
    },
    [daySegments, stationDays]
  );

  // Initialize map once (no tile layer here — managed by viewMode effect)
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const europeBounds = L.latLngBounds(
      L.latLng(34, -12),  // SW corner (southern Portugal / Morocco strait)
      L.latLng(72, 40)    // NE corner (northern Scandinavia / eastern Finland)
    );

    prevRouteKeyRef.current = "";
    const map = L.map(containerRef.current, {
      center: [48, 10],
      zoom: 5,
      minZoom: 4,
      maxBounds: europeBounds,
      maxBoundsViscosity: 1,
      zoomControl: false,
      attributionControl: false,
    });

    // ── MapRenderer: panes + layer groups ───────────────────────────
    const mgr = new MapRenderer(map);
    mgrRef.current = mgr;
    mapRef.current = map;

    // Expose raw map instance (for train animation hook)
    onMapRefRef.current?.(map);

    // Expose zoom controls to parent
    onMapReady?.({
      zoomIn: () => map.zoomIn(),
      zoomOut: () => map.zoomOut(),
    });

    // Leaflet ignores fit requests during an animated zoom. Run only the newest
    // requested viewport once that zoom finishes, including quick train acceptance.
    map.on("zoomstart", () => { zoomingRef.current = true; });
    map.on("zoomend", () => {
      zoomingRef.current = false;
      const pending = pendingFitRef.current;
      pendingFitRef.current = null;
      pending?.();
    });

    // ── Custom attribution control (CARTO + OSM) ─────────────────
    const Attribution = L.Control.extend({
      options: { position: "bottomright" as L.ControlPosition },
      onAdd() {
        const el = L.DomUtil.create("div");
        el.style.cssText =
          "font-size:10px;color:rgba(0,0,0,0.45);background:rgba(255,255,255,0.7);" +
          "padding:2px 6px;border-radius:4px;pointer-events:auto;";
        el.innerHTML =
          (cartoKey ? '© <a href="https://carto.com/attribution" target="_blank" rel="noopener" style="color:inherit">CARTO</a> ' : '') +
          '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener" style="color:inherit">OpenStreetMap</a>';
        L.DomEvent.disableClickPropagation(el);
        return el;
      },
    });
    new Attribution().addTo(map);

    // Click on empty map area deselects any selected stop
    map.on("click", () => {
      onDeselectStopRef.current();
      onSelectedStopPositionUpdateRef.current?.(null);
    });

    // Cleanup on unmount
    return () => {
      mgr.destroy();
      mgrRef.current = null;
      tileLayerRef.current = null;
      pendingFitRef.current = null;
      zoomingRef.current = false;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ─── Swap tile layer based on viewMode ─────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Remove previous tile layer
    if (tileLayerRef.current) {
      map.removeLayer(tileLayerRef.current);
    }

    // Always use light tiles for both modes
    const tile = L.tileLayer(TILES_LIGHT, { maxZoom: 19 }).addTo(map);
    tileLayerRef.current = tile;

    // Update container bg for loading state
    if (containerRef.current) {
      const el = containerRef.current.querySelector(".leaflet-container") as HTMLElement | null;
      if (el) el.style.background = NEUTRAL.mapBg;
      containerRef.current.style.background = NEUTRAL.mapBg;
    }
  }, [viewMode]);

  // ─── Stable callbacks object for MapRenderer ──────────────────────────
  const callbacksRef = useRef<MarkerCallbacks>({
    onSelectStop: onSelectStop,
    onSelectStopWithPosition: onSelectStopWithPosition,
    onSelectRoute: onSelectRoute,
    onAddRouteToTrip: onAddRouteToTrip,
    onSearchConnection,
    onSetHoveredPOI: onSetHoveredPOI,
    onClearHoveredPOI: onClearHoveredPOI,
  });
  callbacksRef.current = {
    onSelectStop,
    onSelectStopWithPosition,
    onSelectRoute,
    onAddRouteToTrip,
    onSearchConnection,
    onSetHoveredPOI,
    onClearHoveredPOI,
  };

  // ─── Render all map layers ─────────────────────────────────────────
  // One effect, one clear, seven method calls. Each is a black box.
  useEffect(() => {
    const mgr = mgrRef.current;
    if (!mgr) return;
    const cb = callbacksRef.current;

    mgr.clearGroup("main");

    // 1. Trip leg polylines (drawn first, underneath everything)
    mgr.renderTripLegs(tripStops, tripLegs, tripStationDayMap, activeRoute, cb);

    // 2. "Day N" badges on the trip line at overnight boundaries
    mgr.renderTripDayBadges(tripStops, tripLegs, tripStationDayMap, activeStationIds, activeCityNames, activeRoute);

    // 3. Trip stop markers (only stops NOT on the active search route)
    mgr.renderTripStops(tripStops, tripLegs, tripStationDayMap, stopActions, selectedStop, { ids: activeStationIds, cities: activeCityNames }, cb);

    // 3b. Intermediate station markers along each trip leg
    mgr.renderTripLegStations(tripStops, tripLegs, tripStationDayMap, activeStationIds, activeCityNames, cb);

    // 4. Inactive search-route polylines + 6. Active route
    mgr.renderInactiveRoutes(routes, activeRouteIndex, cb);
    if (activeRoute) mgr.renderActiveRoute(activeRoute, daySegments, stopActions, selectedStop, getDayColor, getDayNumber, cb);
  }, [
    routes,
    activeRouteIndex,
    activeRoute,
    activeStationIds,
    activeCityNames,
    selectedStop,
    stopActions,
    daySegments,
    getDayColor,
    getDayNumber,
    viewMode,
    tripStops,
    tripLegs,
    tripStationDayMap,
    exploreFilters,
  ]);

  // ─── POI markers (explore + search mode) ───────────────────────────
  useEffect(() => {
    const mgr = mgrRef.current;
    if (!mgr) return;
    mgr.renderPOIs(viewMode, allPOIs, routePOIs, tripPOIs, exploreFilters, callbacksRef.current);
  }, [viewMode, allPOIs, routePOIs, tripPOIs, exploreFilters]);

  // Fit bounds when route changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const route = routes[activeRouteIndex];
    const geometryKey = route ? `${route.id}:${activeRouteIndex}` : tripStops.map(stop => stop.id).join(":") + tripLegs.map(leg => leg.routeId).join(":");
    const routeKey = `${geometryKey}:${searchPanelVisible}:${tripPanelVisible}`;
    if (routeKey === prevRouteKeyRef.current) return;
    prevRouteKeyRef.current = routeKey;

    const savedCoordinates = tripLegs.flatMap(leg => leg.routeCoords ?? []);
    const stations = route ? (route.stationData ?? route.stations.map(getCityById)) : tripStops;
    const stationCoordinates = stations.filter((station): station is City => !!station)
      .map(station => [station.latitude, station.longitude] as [number, number]);
    const savedPath = [...savedCoordinates, ...stationCoordinates];
    const coords = route ? (route.routeCoords ?? stationCoordinates) : savedPath;

    if (coords.length === 0) {
      pendingFitRef.current = null;
      return;
    }
    const fit = () => {
      const bounds = L.latLngBounds(coords);
      const width = map.getSize().x;
      map.stop();
      map.fitBounds(bounds, {
        paddingTopLeft: [searchPanelVisible ? Math.min(372, width * 0.42) : 24, 70],
        paddingBottomRight: [tripPanelVisible ? Math.min(340, width * 0.38) : 24, 90],
        maxZoom: 8,
        animate: true,
        duration: 0.8,
      });
    };
    if (zoomingRef.current) pendingFitRef.current = fit;
    else fit();
  }, [routes, activeRouteIndex, tripStops, tripLegs, searchPanelVisible, tripPanelVisible]);

  // Pan to selected stop
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedStop) return;

    map.panTo([selectedStop.latitude, selectedStop.longitude], {
      animate: true,
      duration: 0.5,
    });

    const currentZoom = map.getZoom();
    if (currentZoom < 7) {
      map.setZoom(7, { animate: true });
    }
  }, [selectedStop]);

  // Keep discovery card anchored to its marker as the map pans or zooms
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedStop) return;

    const updatePos = () => {
      const pos = map.latLngToContainerPoint([selectedStop.latitude, selectedStop.longitude]);
      onSelectedStopPositionUpdateRef.current?.({ x: pos.x, y: pos.y });
    };

    updatePos();
    map.on("move", updatePos);
    map.on("zoom", updatePos);
    return () => {
      map.off("move", updatePos);
      map.off("zoom", updatePos);
    };
  }, [selectedStop]);

  // Spider-leg POI connections removed — connections layer kept clear
  useEffect(() => {
    const mgr = mgrRef.current;
    if (!mgr) return;
    mgr.getGroup("connections").clearLayers();
  }, [poiConnections]);

  return (
    <>
      <style>{`
        @keyframes marker-pulse {
          0% { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(2); opacity: 0; }
        }
        /* Pulse ring hidden by default; shown on hover or when card is open */
        .pulse-ring {
          display: none;
          animation: marker-pulse 1.5s ease-out infinite;
        }
        .pinned-card:hover .pulse-ring,
        .pinned-card.is-selected .pulse-ring {
          display: block;
        }
        .pinned-card {
          pointer-events: auto;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .pinned-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.18) !important;
        }
        .leaflet-container {
          background: ${NEUTRAL.mapBg};
          font-family: inherit;
        }
        .explore-rich-tooltip {
          background: white !important;
          border: none !important;
          border-radius: 8px !important;
          padding: 0 !important;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
          max-width: 280px !important;
          overflow: hidden !important;
          white-space: normal !important;
        }
        .explore-rich-tooltip .leaflet-tooltip-content {
          margin: 0 !important;
        }
        .explore-rich-tooltip::before {
          display: none !important;
        }
      `}</style>
      <div
        ref={containerRef}
        style={{ height: "100vh", width: "100vw" }}
      />
    </>
  );
}
